import React, {useEffect, useState} from "react";
import {Box, DialogTitle, Paper, Step, StepButton, Stepper} from "@mui/material";
import {useResources} from "../../stores/ResourceProvider";
import User from "../../model/User";
import {isNumber} from "../../model/ModelUtilities";
import Model from "../../model/Model";
import Review, {ReviewItem, ReviewStatus} from "../../model/Review";
import TimelineReview from "./TimelineReview";
import IModelItem from "../../model/IModelItem";
import NetWorthReview from "./NetWorthReview";
import BudgetReview from "./BudgetReview";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import {UpdateReviewInput, UserType} from "../../API";
import ReviewChecklist from "./ReviewChecklist";
import {useNavigate} from "react-router-dom";
import RoutesConfig from "../../RoutesConfig";

export enum ReviewStep {
  Checklist = "checklist",
  Timeline = "timeline",
  Networth = "networth",
  Budget = "budget",
  Complete = "complete"
}

const ReviewDialog = ({
  review,
  onClose,
  onDecline,
  onSubmit
}: {
  review: Review
  onClose?: () => any
  onDecline?(review: Review): void
  onSubmit?: (review: Review) => any
}) => {
  const { userStore, modelStore, confirm } = useResources()
  const navigate = useNavigate()

  const [client, setClient] = useState<User | undefined>()
  const [isClient, setIsClient] = useState<boolean>(false)
  const [currentReview, setCurrentReview] = useState<Review | undefined>()
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [currentUpdatedAt, setCurrentUpdatedAt] = useState<string>("")
  const [view, setView] = useState<string>(ReviewStep.Checklist)
  const [viewNames, setViewNames] = useState<string[]>([])
  const [year, setYear] = useState<number | undefined>()

  const isLoading = !view || view === "loading"

  useEffect(() => {
    const viewNames = [ReviewStep.Checklist, ReviewStep.Timeline, ReviewStep.Networth, ReviewStep.Budget, ReviewStep.Complete]
    setViewNames(viewNames)

    const viewName = ReviewStep.Checklist
    const year = new Date().getFullYear().toString()

    if (viewName && viewNames.indexOf(viewName) >= 0) {
      setView(viewName)
      if (year) {
        const yearNum = parseInt(year)
        if (isNumber(yearNum)) {
          setYear(yearNum)
        }
      }
    }

    setIsClient(userStore.user !== undefined && userStore.user.userType === UserType.Client)

    if (!userStore.isLoading && !modelStore.isLoading && userStore.user) {
      loadClient()
    }
  }, [userStore.isLoading, modelStore.isLoading, modelStore])

  useEffect(() => {
    if (!isLoading && currentReview && currentReview.modelId) {
      loadModel(currentReview.modelId)
    }

  }, [currentReview, currentModel, currentUpdatedAt])
  
  const loadClient = async () => {
    let user : User | undefined
    if (userStore.user) {
      user = userStore.user
    }
    if (user) {
      setClient(user)
      setView("checklist")
      if (review) {
        await loadReview(review)
      }
    }
  }

  const loadReview = async (review: Review) => {
    setCurrentReview(review)
    if (review.modelId) {
      const model = await modelStore.getCurrentModel(review.modelId)
      if (model) {
        setCurrentModel(model)
      }
      // let step = view
      // if (!review.isItemCompleted(ReviewItem.Questionaire) || !review.isItemCompleted(ReviewItem.TaxReturn)) {
      //   step = ReviewStep.Checklist
      // } else if (!review.isItemCompleted(ReviewItem.Timeline)) {
      //   step = ReviewStep.Timeline
      // } else if (!review.isItemCompleted(ReviewItem.AssetsAndLiabilities)) {
      //   step = ReviewStep.Networth
      // } else if (!review.isItemCompleted(ReviewItem.Budget)) {
      //   step = ReviewStep.Budget
      // } else {
      //   step = ReviewStep.Complete
      // }
      setView(ReviewStep.Checklist)
    }
  }

  const loadModel = async (modelId: string) => {
    const model = await modelStore.getCurrentModel(modelId)
    if (model) {
      setCurrentModel(model)
      setCurrentUpdatedAt(model.updatedAt)
    }
  }

  // const onViewChange = (view: string) => {
  //   setView(view)
  // }

  // const handleChangeReview = async (review: Review) => {
  //   setCurrentReview(review)
  // }

  // const handleSaveReview = async (review: Review) => {
  //   setCurrentReview(review)
  // }

  const handleClose = () => {
    if (currentReview && currentReview.status !== ReviewStatus.Submitted) {
      navigate(RoutesConfig.signOut.pathname)
    } else if (onClose) {
      onClose()
    }
  }

  const handleDecline = async () => {
    if (onDecline) {
      confirm.show('Confirm Decline', `Are you sure you want to decline this review?`,
        ['Decline', 'Cancel'],
        async () => {
          const input: UpdateReviewInput = {
            id: review.id,
            status: ReviewStatus.Declined
          }
          await userStore.updateReview(input)
          onDecline(review)
          return true
        })
    }
  }

  const handleSubmit = (review: Review) => {
    if (onSubmit) {
      onSubmit(review)
    }
  }

  const handleChangeView = (newView: string) => {
    setView(newView)
  }

  const renderStepper = () => {
    const activeStep = viewNames.indexOf(view)

    if (!currentReview) {
      return null
    }

    return (
      <Box width="100%" padding="8px 0">
        <Stepper nonLinear activeStep={activeStep}>
          <Step key={ReviewStep.Checklist} completed={currentReview.isItemCompleted(ReviewItem.Questionaire) && currentReview.isItemCompleted(ReviewItem.TaxReturn)}>
            <StepButton onClick={() => handleChangeView(ReviewStep.Checklist)}>
              Checklist
            </StepButton>
          </Step>
          <Step key={ReviewStep.Timeline} completed={currentReview.isItemCompleted(ReviewItem.Timeline)}>
            <StepButton onClick={() => handleChangeView(ReviewStep.Timeline)}>
              Timeline
            </StepButton>
          </Step>
          <Step key={ReviewStep.Networth} completed={currentReview.isItemCompleted(ReviewItem.AssetsAndLiabilities)}>
            <StepButton onClick={() => handleChangeView(ReviewStep.Networth)}>
              Assets & Liabilities
            </StepButton>
          </Step>
          <Step key={ReviewStep.Budget} completed={currentReview.isItemCompleted(ReviewItem.Budget)}>
            <StepButton onClick={() => handleChangeView(ReviewStep.Budget)}>
              Budget
            </StepButton>
          </Step>
          <Step key={ReviewStep.Complete} completed={currentReview.status === ReviewStatus.Submitted}>
            <StepButton onClick={() => handleChangeView(ReviewStep.Complete)}>
              Complete
            </StepButton>
          </Step>
        </Stepper>
      </Box>
    )
  }

  return(
    <Dialog
      id="annualReviewDialog"
      open={true}
      onClose={() => {}}
      scroll="paper"
      maxWidth="lg"
      fullWidth
      aria-labelledby="annualReviewDialogTitle"
    >
      <Paper sx={{width:"100%"}}>
        <DialogTitle id="annualReviewDialogTitle">
          Annual Review
        </DialogTitle>
        <DialogContent sx={{m:0, p:0}}>
          {renderStepper()}
        </DialogContent>
          {view === 'loading' &&
            <Box display="flex" flexDirection="column" justifyContent="space-around" alignItems="center" width="100%">
              <Paper elevation={1} sx={{width:"50%"}}>

              </Paper>
            </Box>
          }
          {view === 'checklist' && currentReview && currentModel &&
            <ReviewChecklist review={currentReview} model={currentModel} updatedAt={currentUpdatedAt} step={ReviewStep.Checklist} isDialog={true}
                             onChange={(review: Review) => setCurrentReview(review)}
                             onChangeView={handleChangeView}
                             onClose={handleClose}
                             onDecline={handleDecline}
                             onSubmit={handleSubmit}
            />
          }
          {view === 'timeline' && currentReview && currentModel &&
            <TimelineReview review={currentReview} model={currentModel} updatedAt={currentUpdatedAt} isDialog={true}
                            onChange={(item: IModelItem) => setCurrentUpdatedAt(item.updatedAt)}
                            onChangeView={handleChangeView}
                            onClose={handleClose}
                            onDecline={handleDecline}
            />
          }

          {view === 'networth' && currentReview && currentModel &&
            <NetWorthReview review={currentReview} model={currentModel} updatedAt={currentUpdatedAt} isDialog={true}
                            onChange={(item: IModelItem) => {
                              setCurrentUpdatedAt(item.updatedAt)
                            }}
                            onChangeView={handleChangeView}
                            onClose={handleClose}
                            onDecline={handleDecline}
            />
          }

          {view === 'budget' && currentReview && currentModel &&
            <BudgetReview review={currentReview} model={currentModel} updatedAt={currentUpdatedAt} isDialog={true}
                          onChange={(item: IModelItem) => setCurrentUpdatedAt(item.updatedAt)}
                          onChangeView={handleChangeView}
                          onClose={handleClose}
                          onDecline={handleDecline}
            />
          }
          {view === 'complete' && currentReview && currentModel &&
            <ReviewChecklist review={currentReview} model={currentModel} updatedAt={currentUpdatedAt} step={ReviewStep.Complete} isDialog={true}
                             onChange={(review: Review) => setCurrentReview(review)}
                             onChangeView={handleChangeView}
                             onClose={handleClose}
                             onDecline={handleDecline}
                             onSubmit={handleSubmit}
            />
          }
      </Paper>
    </Dialog>
  )
}

export default ReviewDialog